import type { PagePoint, ReadingReviewOut, ReviewWall } from "@/api";
import { Button } from "@/components/Button";
import { StateMessage } from "@/components/StateMessage";
import { pageFrame, type PageImage } from "@/lib/usePageImage";

import styles from "./Review.module.css";

interface OriginalPaneProps {
  image: PageImage;
  /** The page size the reading names (plan 4 §9.1) — the frame every overlay is drawn in. */
  page: Pick<ReadingReviewOut, "pageWidthPx" | "pageHeightPx"> | null;
  walls: ReviewWall[];
  activeWallId: string | null;
  simplifications: ReadingReviewOut["simplifications"];
  /** The walls the ring's hint names (§10.1): marked like a highlighter on paper. */
  hinted?: ReadonlySet<string>;
  onHover?: (wallId: string | null) => void;
  onPick?: (wallId: string) => void;
  /** A reading of the form laid over the sheet — the „Form nicht sicher“ view. */
  outline?: { points: PagePoint[]; closed: boolean } | null;
}

/**
 * The sheet itself, as the readings saw it (plan 4 §5) — page 1 from the
 * page route, turned the way the worker turned it.
 *
 * Quiet on purpose: of all the walls only the **active** one is drawn, ink
 * on a white edge so it stands on any pencil. Fifteen coloured lines over a
 * customer's sketch would hide the very handwriting a person is checking.
 * The places the form check straightened stay marked as dashed rings. Where
 * the ring's hint names walls („einer von W1, W3, W5, W7 stimmt nicht“),
 * they lie on a broad translucent stroke that multiplies into the paper —
 * the pencil stays readable through it, as under a highlighter.
 *
 * Drawn as one SVG in the page's own pixels, image and overlay together, so
 * they cannot drift apart at any size.
 */
export function OriginalPane({
  image,
  page,
  walls,
  activeWallId,
  simplifications,
  hinted,
  onHover,
  onPick,
  outline,
}: OriginalPaneProps) {
  if (image.status === "failed") {
    return (
      <div className={styles.paneMessage}>
        <StateMessage
          title="Original nicht geladen."
          live={false}
          action={
            <Button variant="leise" flush onClick={image.retry}>
              Neu laden
            </Button>
          }
        >
          Die Liste daneben bleibt bedienbar.
        </StateMessage>
      </div>
    );
  }
  if (image.status === "loading") {
    return (
      <div className={styles.paneWait} role="status">
        Original wird geladen …
      </div>
    );
  }

  const { width, height } = pageFrame(page, image);
  const longer = Math.max(width, height);
  const active = walls.find((wall) => wall.wallId === activeWallId) ?? null;
  const toPx = ([x, y]: PagePoint) => `${x * width},${y * height}`;
  const marked = hinted && hinted.size > 0 ? walls.filter((wall) => hinted.has(wall.wallId)) : [];

  // The customer's page is a sheet (design 2026-10-24 §5.3): paper in the
  // dark, the field's head and foot around it in the mode. The window round
  // the page and the marks on it are ours — warm paper, as the proposal
  // beside it (B-20).
  return (
    <div className={styles.paneBox} data-blatt="zeichnung">
      <svg
        className={styles.original}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Original, Seite 1"
        onPointerLeave={onHover ? () => onHover(null) : undefined}
      >
        <image href={image.src} x={0} y={0} width={width} height={height} preserveAspectRatio="none" />

        {marked.map((wall) => (
          <line
            key={`hint-${wall.wallId}`}
            className={styles.hintMarkPaper}
            data-role="original-hint"
            data-wall={wall.wallId}
            x1={wall.start[0] * width}
            y1={wall.start[1] * height}
            x2={wall.end[0] * width}
            y2={wall.end[1] * height}
            strokeWidth={longer * 0.012}
          />
        ))}

        {simplifications.map((place, index) => (
          <circle
            key={index}
            className={styles.simplified}
            cx={place.at[0] * width}
            cy={place.at[1] * height}
            r={longer * 0.022}
            vectorEffect="non-scaling-stroke"
          >
            <title>{place.kind === "fase" ? "Fase begradigt" : "Rücksprung begradigt"}</title>
          </circle>
        ))}

        {outline ? (
          <g data-role="original-outline">
            <polyline
              className={styles.outlineEdge}
              points={[...outline.points, ...(outline.closed ? [outline.points[0]!] : [])].map(toPx).join(" ")}
              vectorEffect="non-scaling-stroke"
            />
            <polyline
              className={styles.outlineLine}
              points={[...outline.points, ...(outline.closed ? [outline.points[0]!] : [])].map(toPx).join(" ")}
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ) : null}

        {active ? (
          <g data-role="original-active" data-wall={active.wallId}>
            <line
              className={styles.activeEdge}
              x1={active.start[0] * width}
              y1={active.start[1] * height}
              x2={active.end[0] * width}
              y2={active.end[1] * height}
              vectorEffect="non-scaling-stroke"
            />
            <line
              className={styles.activeLine}
              x1={active.start[0] * width}
              y1={active.start[1] * height}
              x2={active.end[0] * width}
              y2={active.end[1] * height}
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ) : null}

        {onHover
          ? walls.map((wall) => (
              <line
                key={wall.wallId}
                className={styles.hit}
                data-role="original-wall"
                data-wall={wall.wallId}
                x1={wall.start[0] * width}
                y1={wall.start[1] * height}
                x2={wall.end[0] * width}
                y2={wall.end[1] * height}
                vectorEffect="non-scaling-stroke"
                onPointerEnter={() => onHover(wall.wallId)}
                onClick={onPick ? () => onPick(wall.wallId) : undefined}
              >
                <title>{wall.label}</title>
              </line>
            ))
          : null}
      </svg>
    </div>
  );
}
